import type { ReactNode } from 'react';
import type { MantineColor } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import Noty from 'noty';

type TTypeNotification = 'success' | 'error' | 'info' | 'warning';

type TOptionsNoty = {
  /**
   * Plain-string notification body. Required because the legacy `Noty` path
   * renders an HTML string and cannot accept a `ReactNode`.
   *
   * @remarks
   * TODO: once the redesign ships and `MODE_FEATURE_FLAG_REDESIGN_MANTINE` is
   * removed, drop the legacy `Noty` branch, replace every `text` usage with
   * `message` at the call sites, and remove this field.
   */
  text: string;
  /**
   * Rich notification body for the Mantine path; falls back to `text` when
   * omitted. Prefer this for any new code — it is the format to keep after the
   * feature flag is removed.
   */
  message?: ReactNode;
  type?: TTypeNotification;
  title?: ReactNode;
};

const TYPE_TO_COLOR: Record<TTypeNotification, MantineColor> = {
  success: 'green.7',
  error: 'red.6',
  info: 'indigo.6',
  warning: 'orange.5',
};

/**
 * TODO: rename to `toast` once `MODE_FEATURE_FLAG_REDESIGN_MANTINE` is removed
 * — the name `noty` is a leftover from the legacy `noty` library, which the
 * flag-off branch is the last user of.
 */
export default function noty({
  text,
  message,
  type = 'info',
  title,
}: TOptionsNoty) {
  if (import.meta.env.MODE_FEATURE_FLAG_REDESIGN_MANTINE === 'on') {
    notifications.show({
      withBorder: true,
      color: TYPE_TO_COLOR[type],
      title,
      message: message ?? text,
    });

    return;
  }

  // Legacy noty has no title/loading concept — it only renders text by type.
  // Those states only ship on the Mantine (flag-on) path.
  new Noty({
    theme: 'bootstrap-v3',
    timeout: 3000,
    type,
    text,
  }).show();
}
